import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLightbulb, FaPlug, FaThermometerHalf, FaPowerOff } from 'react-icons/fa';
import { BsDoorClosed } from 'react-icons/bs';
import axios from 'axios';
import { motion } from 'framer-motion';

// Animación para iconos al encenderse
const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
`;

// Función para asignar iconos según el tipo de dispositivo
const getDeviceIcon = (type, isAnimating) => {
  const className = `device-icon ${isAnimating ? 'animate' : ''}`;
  switch (type) {
    case 'light':
      return <FaLightbulb className={className} />;
    case 'plug':
      return <FaPlug className={className} />;
    case 'thermostat':
      return <FaThermometerHalf className={className} />;
    case 'door':
      return <BsDoorClosed className={className} />;
    default:
      return <FaLightbulb className={className} />;
  }
};

const DeviceCard = ({ id, name, type, state: initialState, firstLoad }) => {
  const [deviceState, setDeviceState] = useState(initialState);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(!firstLoad); // Controla la animación de entrada
  const [lastState, setLastState] = useState(initialState); // Guarda el estado anterior

  useEffect(() => {
    if (lastState !== initialState) {
      setDeviceState(initialState);
      setLastState(initialState);
    }
  }, [initialState, lastState]);

  useEffect(() => {
    if (firstLoad) {
      const timeout = setTimeout(() => setHasLoaded(true), 500);
      return () => clearTimeout(timeout);
    }
  }, [firstLoad]);

  const toggleState = async () => {
    const newState = deviceState === 'on' ? 'off' : 'on';

    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}devices/${id}`,
        { state: newState },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDeviceState(newState);

      if (newState === 'on') {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 500);
      }
    } catch (error) {
      console.error('Error al cambiar el estado del dispositivo:', error);
      alert('Hubo un error al cambiar el estado del dispositivo.');
    }
  };

  return (
    <motion.div
      initial={hasLoaded ? false : { opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <StyledCard state={deviceState}>
        <div className="icon-container">{getDeviceIcon(type, isAnimating)}</div>
        <div className="text-content">
          <h3 className="device-title">{name}</h3>
          <p className="device-subtitle">{type}</p>
        </div>
        <button className="power-btn" onClick={toggleState} data-state={deviceState}>
          <FaPowerOff className="power-icon" />
        </button>
      </StyledCard>
    </motion.div>
  );
};

const StyledCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border: 1px solid ${(props) => (props.state === 'on' ? '#d1d1d1' : '#e5e5e5')};
  border-radius: 15px;
  padding: 15px 20px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 4px 12px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.1) 0px 6px 15px;
  }

  .icon-container {
    font-size: 2.8rem;
    color: ${(props) => (props.state === 'on' ? '#4CAF50' : '#b0b0b0')};
    margin-right: 20px;
  }

  .device-icon {
    transition: transform 0.3s ease;
  }

  .device-icon.animate {
    animation: ${pulseAnimation} 0.5s ease-out;
  }

  .text-content {
    flex: 1;
    margin-right: 40px;
  }

  .device-title {
    font-size: 1.2rem;
    font-weight: bold;
    margin: 0;
    color: #333;
  }

  .device-subtitle {
    font-size: 0.9rem;
    margin: 5px 0 0;
    color: #666;
  }

  .power-btn {
    padding: 10px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    border: none;
    background-color: ${(props) => (props.state === 'on' ? '#4CAF50' : '#b0b0b0')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.3s ease;
  }

  .power-btn:hover {
    transform: scale(1.1);
  }

  .power-icon {
    font-size: 1.2rem;
    color: #fff;
  }
`;

export default DeviceCard;